import { useState, useEffect, useRef } from 'react'
import { Props_SnippetVideo } from './Video.props'
import { CompileMarkdown } from '../../../../Shared/AttraxTs/React/ReactSnippetMDCompiler'
import { openConsentPreferences } from '../utils/CookieConsent'
import { useVideoConsent } from '../hooks/useVideoConsent'

type Video = 'youtube' | 'vimeo' | 'video' | null

const setAutoplay = (url: string) => {
    const hasAutoplay = url.includes('autoplay=1')
    if (!hasAutoplay) {
        const separator = url.includes('?') ? '&' : '?'
        return `${url}${separator}autoplay=1`
    }
}

const getVideoMimeType = (url: string): string => {
    const extension = url.split('.').pop()?.toLowerCase()
    switch (extension) {
        case 'mp4':
            return 'video/mp4'
        case 'webm':
            return 'video/webm'
        case 'ogg':
        case 'ogv':
            return 'video/ogg'
        case 'mov':
            return 'video/quicktime'
        default:
            return 'video/mp4'
    }
}

const getVideoType = (url: string): Video => {
    if (!url) return null
    if (url.includes('youtube') || url.includes('youtu.be')) return 'youtube'
    if (url.includes('vimeo')) return 'vimeo'
    return 'video'
}

const SnippetVideo = (props: Props_SnippetVideo) => {
    const { heading, content, video, background, version } = props

    const hasConsent = useVideoConsent();
    const [videoType, setVideoType] = useState<Video>(null)
    const [playing, setPlaying] = useState<boolean>(false)
    const videoRef = useRef<HTMLVideoElement | null>(null)
    const classHeadingVisibility = heading.visibility?.value ? '' : 'snippet-video__heading--hidden'
    const classVideoPlaying = playing ? 'snippet-video--playing' : ''

    const backgroundMap = {
        'default': '#f1f8e9',
        'covea-dark-blue': '#122147',
        'covea-light-green': '#d6eac1',
        'covea-light-red': '#efded3',
        'covea-light-purple': '#e2d3f2',
        'covea-light-turquoise': '#c8eaed',
        'covea-light-yellow': '#eae8ad',
        'MAAF-dark-blue': '#0091a7',
        'MAAF-light-blue': '#e5f3f5',
        'MAAF-light-red': '#fef6f2',
        'MMA-light-green': '#d6eceb',
        'MMA-light-orange': '#f2e1b5',
        'MMA-dark-blue': '#1d3b6d',
        'GMF-dark-blue': '#144390',
        'GMF-light-blue': '#dfedff',
        'GMF-light-orange': '#fff4ed',
        'Fidelia-dark-green': '#269b38',
        'Fidelia-light-blue': '#e1f0fb',
        'Fidelia-light-green': '#ebf2e7'
    }
    
    const backgroundKey = background?.colour.value || 'default'
    const backgroundValue = backgroundMap[backgroundKey as keyof typeof backgroundMap]
    const darkBackgrounds = ['covea-dark-blue', 'MAAF-dark-blue', 'MMA-dark-blue', 'GMF-dark-blue', 'Fidelia-dark-green']
    const useWhiteText = darkBackgrounds.includes(backgroundKey) ? 'snippet-video--white-text' : ''
    
    useEffect(() => {
        setVideoType(video?.url?.value ? getVideoType(video.url.value) : null)
    }, [video?.url?.value])

    
    return (
        <section 
            className={`snippet-video ${classVideoPlaying} ${useWhiteText} snippet-video--${version?.style?.value || 'version-1'}`}
        >
            <div className='snippet-video__decoration' style={{ backgroundColor: backgroundValue }}></div>
            <div className="snippet-video__container">
                <div className="snippet-video__container-content">
                    <h2 className={`snippet-video__heading ${classHeadingVisibility}`}>{heading?.content?.value}</h2>
                    <div className="snippet-video__content">
                        {content.content?.value && CompileMarkdown(content?.content?.value)}
                    </div>
                </div>

                <div className="snippet-video__container-video-player">
                    {video?.thumbnail?.value && (
                        <figure className="snippet-video__container-thumbnail">
                            <img
                                src={video.thumbnail.value}
                                alt={video?.alt?.value || ''}
                                className="snippet-video__thumbnail"
                                loading="lazy"
                                width="1280"
                                height="720"
                            />
                        </figure>
                    )}
                    {video?.url?.value && (
                        <div className="snippet-video__container-video">
                            {video?.thumbnail?.value && (
                                <>
                                    {hasConsent && (
                                        <button
                                            className="snippet-video__button-play"
                                            aria-label={`{{js.snippet.video.button.play.aria-label}}`}
                                            onClick={() => setPlaying(true)}
                                            data-cookie-play='play-with-cookies'
                                        >
                                            <span className="sr-only">{`{{js.snippet.video.button.play.hidden-label}}`}</span>
                                        </button>
                                    )}
                                    {!hasConsent && (
                                        <p className="snippet-video__consent-message">
                                            {`{{js.snippet.video.cookie.info}}`}{' '}
                                            <button onClick={openConsentPreferences}>
                                                {`{{js.snippet.video.cookie.info.trigger}}`}
                                            </button>
                                        </p>
                                    )}
                                </>
                            )}
                            {playing && (videoType === 'youtube' || videoType === 'vimeo') && (
                                <iframe
                                    className="snippet-video__video"
                                    src={setAutoplay(video.url.value)}
                                    width="1280"
                                    height="720"
                                    title={video.alt?.value || `{{js.snippet.video.iframe.title}}`}
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                    referrerPolicy={videoType === 'youtube' ? 'strict-origin-when-cross-origin' : undefined}
                                    allowFullScreen
                                ></iframe>
                            )}
                            {playing && videoType === 'video' && (
                                <video
                                    className="snippet-video__video"
                                    controls
                                    controlsList='nodownload'
                                    width="1280"
                                    height="720"
                                    ref={videoRef}
                                    autoPlay
                                >
                                    <track kind="captions" />
                                    <source src={video.url.value} type={getVideoMimeType(video.url.value)} />
                                    Your browser does not support the video tag.
                                </video>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </section>
    )
}

export default SnippetVideo